import React from 'react';
import dynamic from 'next/dynamic'
import MaterialSnackBar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { equipmentOperations } from '../../../../redux/equipment';
import { cartOperations } from '../../../../redux/cart';
import ExpandedDetails from '../../../../components/ExpandedProductDetails';
import { useAuth } from '@developers/single-sign-on';
import { useRouter } from 'next/router';
import { config } from '../../../../utils/config';
import * as diagramsAjax from '../../../../ajax/diagrams';
import * as productsAjax from '../../../../ajax/products';
import * as equipmentAjax from '../../../../ajax/equipment';
import { useAjax } from '@developers/use-ajax';
import Link from 'next/link';

export function partFormatter(product) {
  return {
    thumbnail: product.file_small,
    display_name: product.product_name,
    display_number: product.product_id,
    price: product.price?.current,
    original_price: product.price?.original,
    nla: product.product_status == 'NLA',
    internal: product.product_status == 'internal',
    description: product.product_description,
    qty_on_hand: product.qty?.available,
    unit_of_sale: product.product_unit,
  }
}

function adaptProducts(products) {
  if (products) {
    return products.map((product) => {
      let adaptedProduct = {...product.details}
      if (!product.product_id) {
        adaptedProduct.bubble_number = product.bubble_number;
        adaptedProduct.manufacturer_part_number = product.manufacturer_part_number;
        adaptedProduct.missing = true;
        return adaptedProduct;
      }

      adaptedProduct.id = product.product_id;
      adaptedProduct.display_name = product.display_name;
      adaptedProduct.bubble_number = product.bubble_number;
      adaptedProduct.bubble_qty = product.bubble_qty;
      return adaptedProduct;
    });
  }
  return [];
}

function adaptDiagrams(diagrams) {
  return diagrams.map((diagram) => ({
    id: diagram.equipment_diagram_id,
    name: diagram.equipment_diagram_name,
    description: diagram.equipment_diagram_description,
    thumbnail: null,
    equipment_id: diagram.equipment_id,
    tiles_url: diagram.tiles_url,
  }));
}

/** 
 * diagram-viewer uses tile-viewer which uses hammer.js.
 * 
 * hammer.js uses `window` and `document` at **import** time. So conditial render doesn't help.
 * We have to use a dynamic import and tell Next.js to not even load it on the server.
 * 
 * @see https://github.com/hammerjs/hammer.js/issues/930
 */
const DiagramViewer = dynamic(() => import('@developers/diagram-viewer/DiagramViewer'), { ssr: false })
const DiagramHeader = dynamic(() => import('@developers/diagram-viewer/DiagramHeader'), { ssr: false })
const DiagramSidebar = dynamic(() => import('@developers/diagram-viewer/DiagramSidebar'), { ssr: false })
const DiagramNotes = dynamic(() => import('@developers/diagram-viewer/DiagramNotes'), { ssr: false })
const Diagram = dynamic(() => import('@developers/diagram-viewer/Diagram'), { ssr: false })

const mapStateToProps = function(state) {
  return {
    meta: state.getIn(['equipment', 'meta']),
  };
};

const mapDispatchToProps = function (dispatch) {
  return bindActionCreators({
    addToCart: cartOperations.addToCart,
    clearCartMessage: equipmentOperations.clearCartMessage,
  }, dispatch)
};

function Diagrams(props) {
  const router = useRouter();
  const auth = useAuth();
  const [addedProduct, setAddedProduct] = React.useState({id: null, qty: null});
  const [equipment, getEquipment, getEquipmentMeta] = useAjax(equipmentAjax.getEquipment, {}, true);
  const [diagrams, getDiagrams, getDiagramsMeta] = useAjax(diagramsAjax.getDiagramsForEquipment, [], true);
  const [parts, getParts, getPartsMeta] = useAjax(diagramsAjax.getDiagramParts, [], true);
  const [notes, getNotes] = useAjax(diagramsAjax.getDiagramNotes, []);
  const [adminProductDetails, getAdminProductDetails, getAdminProductDetailsMeta] = useAjax(productsAjax.getProductDetails, []);

  const loading = getEquipmentMeta.loading || getPartsMeta.loading || getDiagramsMeta.loading;
  const isAdmin = auth.session_data.admin;

  React.useEffect(() => {
    if (router.query.id && router.query.equipment_id) {
      getEquipment(router.query.equipment_id);
      getDiagrams(router.query.equipment_id);
      getParts(router.query.id);
    }
  }, [router.query.id, router.query.equipment_id]);

  React.useEffect(() => {
    if (isAdmin && router.query.id) {
      getNotes(router.query.id);
    }
  }, [isAdmin, router.query.id]);

  const addToCart = (id, qty) => {
    setAddedProduct({id: id, qty: qty});
    props.addToCart(id, qty);
  }

  async function addNote(id, text) {
    const res = await diagramsAjax.addDiagramNote(id, text);
    if (res.ok) {
      getNotes(router.query.id);
    }
  }

  const fileHost = config('file_host');

  // Breakpoint for small devices (taken from Material UI breakpoints)
  const smallScreenBreakpoint = 960;
  const smallScreen = (typeof window !== 'undefined' && window.innerWidth <= smallScreenBreakpoint);

  const goToEquipment = () => {
    window.location.href = fileHost + '/equipment/Select/' + equipment.equipment_id;
  }

  const goToProduct = (id) => {
    window.location.href = fileHost + '/cart/ViewItem/' + id;
  }

  const goToDiagram = (diagramId) => {
    router.push('/equipment/' + router.query.equipment_id + '/diagrams/' + diagramId);
  }
  
  if ((!loading && diagrams.length == 0) || (!loading && !isAdmin && equipment.equipment_status == 'inactive')) {
    return (
      <div style={{margin: 15}}>
        <h1>Error: Invalid diagram</h1>
        <div style={{textAlign: 'center', margin: 2}}>There is no diagram here. If you found this page from a link, it may have been changed, deleted, or inactive.</div>
      </div>
    );
  } else {
    return (
      <>
        {isAdmin ?
          <p style={{marginBottom: 10}}>
            <a href={'/admin_diagram.php?equipment_diagram_id=' + router.query.id + '&action=edit'}>Click To Edit This Diagram</a>
            <span> - </span>
            <a href={'/admin_diagram.php?equipment_diagram_id=' + router.query.id}>Click To Manage This Diagram</a>
          </p>
        : null}
        <DiagramViewer 
          equipment_name={equipment.equipment_name}
          selected_diagram_id={router.query.id}
          diagrams={adaptDiagrams(diagrams)}
          diagram_parts={adaptProducts(parts)}
          handleDiagramChange={goToDiagram}
          goToEquipment={goToEquipment}
          goToProduct={goToProduct}
          diagram_loading={getDiagramsMeta.loading}
          parts_loading={getPartsMeta.loading}
          addToCart={addToCart}
          condensed={smallScreen}
          showExpandedDetails={isAdmin}
          showMissingParts={isAdmin}
          getPartDetails={productsAjax.getProduct}
          partFormatter={partFormatter}
          notes={notes}
          addNote={addNote}
          expandedDetails={(id) => (
            <ExpandedDetails 
              productId={id}
              product={adminProductDetails}
              getProductDetails={getAdminProductDetails}
              loading={getAdminProductDetailsMeta.loading}
            />
          )} 
        >
          <div className='container-fluid' style={{marginBottom: 15}}>
            <div className='row'>
              <div className='col-sm-12'>
                <DiagramHeader />
              </div>
            </div>
            <div className='row'>
            {smallScreen ?
              <div className='col-sm-12' style={{paddingRight: 10}}>
                <DiagramSidebar />
              </div>
            : 
              <>
              <div className='col-sm-4' style={{paddingRight: 10}}>
                <DiagramSidebar />
              </div>
              <div className='col-sm-8' style={{paddingLeft: 10}}>
                <Diagram />
              </div>
              </>
            }
            </div>
            {isAdmin ?
              <div className='row' style={{marginTop: 20}}>
                <div className='col-sm-12'>
                  <DiagramNotes />
                </div>
              </div>
            : null}
          </div>
        </DiagramViewer >
        <MaterialSnackBar open={props.meta.showMessage} autoHideDuration={4000} onClose={props.clearCartMessage}>
          <SnackbarContent 
            message={
              <div>
                Qty {addedProduct.qty} of part {addedProduct.id} was added to your <Link href='/cart' passHref><a style={{ color: 'yellow', textDecoration: 'underline' }}>cart</a></Link>
              </div>
            } 
          />
        </MaterialSnackBar>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Diagrams);
